import type { WidgetNodeProps } from "@/pages/canvas/types/canvasNodeData";
import type { Node } from "@xyflow/react";
import type React from "react";

export interface WidgetPreset {
  id: string;
  label: string;

  /**
   * Internal presets are registered for lookup/rendering (so a seeded
   * node can resolve its definition) but are NOT user-placeable — the
   * widget selector popover hides them.
   */
  internal?: boolean;

  // ── Live rendering ────────────────────────────────────────────────────────
  /**
   * HTML string rendered via dangerouslySetInnerHTML on the canvas.
   * Used by WidgetNodeImpl (decorative / HTML widgets). Omit for React-component widgets.
   */
  html?: string;
  /**
   * The React component that renders this widget's content on the canvas.
   * When present, WidgetNode wraps it in ReactWidgetShell instead of using
   * the HTML / CSS-scale path.
   */
  component?: React.ComponentType<WidgetNodeProps>;

  // ── Popover preview ───────────────────────────────────────────────────────
  /**
   * Static HTML shown as the thumbnail in the widget selector popover.
   * For HTML widgets this is optional — falls back to `html`.
   * For React-component widgets this is required (no live html to fall back to).
   */
  previewHtml?: string;
  /**
   * Override the auto-computed preview scale in the widget selector popover.
   * Useful when the preset's defaultHeight makes the auto-scale too small.
   */
  previewScale?: number;

  // ── Toolbar ───────────────────────────────────────────────────────────────
  /**
   * Optional extra toolbar button(s) rendered beside the standard delete button.
   * Receives { id, data } so it can call React hooks for runtime actions
   * (e.g. AI summarize that needs Loro cards + navigation).
   */
  toolbarSlot?: React.ComponentType<{
    id: string;
    data: Record<string, unknown>;
  }>;

  // ── Resize ────────────────────────────────────────────────────────────────
  /**
   * When set, ReactWidgetShell renders a NodeResizer with these bounds.
   * Omit for fixed-size widgets (timer, music player).
   */
  resizable?: {
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    /**
     * When false, the component fills the node via w-full/h-full rather than
     * using CSS scale. Use for components that reflow to their container size
     * (e.g. RetroBoardWidget). Default: true (CSS scale applied).
     */
    scaleContent?: boolean;
  };

  // ── Existing fields (unchanged) ───────────────────────────────────────────
  defaultWidth: number;
  defaultHeight: number;
  /**
   * Optional function to regenerate the HTML string from current node data.
   * Used by interactive HTML widgets when the user fires a widget action —
   * Loro data is updated AND the html field is overwritten with fresh markup.
   */
  rebuildHtml?: (data: Record<string, unknown>) => string;
  /** Extra fields merged into the Loro node on first placement. */
  initialData?: Record<string, unknown>;
  /**
   * React Flow / Loro node zIndex. Use a negative value (e.g. -1) for
   * background widgets that should always sit below all canvas content.
   */
  zIndex?: number;
  /**
   * Per-preset overrides for React Flow node-level behavior flags. Merged
   * onto the Node object at placement time and persisted to Loro. Most
   * widgets omit this and inherit the canvas defaults (selectable,
   * deletable, draggable, focusable all true). Set fields here to opt
   * out — e.g. an onboarding card that should not show a selection
   * outline or a trash toolbar sets
   * `nodeProps: { selectable: false, deletable: false }`.
   *
   * WidgetNode also gates its floating toolbar on `deletable !== false`,
   * so disabling either flag is sufficient to suppress the delete UI.
   */
  nodeProps?: Partial<
    Pick<Node, "selectable" | "deletable" | "draggable" | "focusable">
  >;
}

/** System font stack used by widgets that embed their own text. */
export const FONT = `-apple-system, BlinkMacSystemFont, 'Inter', sans-serif`;
